import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { compare, type Compared } from '../period/compare';
import { tileLabel, type PeriodChoice } from '../period/period';
import { COST_ESTIMATE_EXPLAINED, MARGIN_EXPLAINED, costWords } from './CostEstimate';
import { margin } from './revenue-math';
import type { Revenue } from './revenue-types';

function Figure(props: {
  label: string;
  value: ReactNode;
  compared?: Compared | undefined;
  /** One more short line: the cost a page, the percent. */
  note?: ReactNode;
  title?: string;
  lead?: boolean;
}): ReactNode {
  const { compared } = props;
  return (
    <div className={`ad-rfig${props.lead ? ' ad-rfig--lead' : ''}`} title={props.title}>
      <span className="ad-rfig__label">{props.label}</span>
      <strong className="ad-rfig__value">{props.value}</strong>
      {props.note ? <span className="ad-rfig__note">{props.note}</span> : null}
      {compared ? (
        <span className="ad-rfig__compare">
          <span className={`ad-tone--${compared.tone}`}>{compared.change}</span> {compared.before}
        </span>
      ) : null}
    </div>
  );
}

/** Net less processing cost, in dollars and as a share of net; a quiet "no revenue" when nothing was kept. */
function MarginFigure({ revenue, label }: { revenue: Revenue; label: string }): ReactNode {
  const f = useFormat();
  const { totals, previous, period } = revenue;
  const percent = margin(totals);
  if (percent === null) {
    return (
      <Figure
        label={label}
        title={MARGIN_EXPLAINED}
        value={<span className="ad-rfig__none">no revenue</span>}
      />
    );
  }
  const before = margin(previous);
  return (
    <Figure
      label={label}
      title={MARGIN_EXPLAINED}
      value={f.money(totals.net - totals.cost)}
      note={`${f.percent(percent, 0)} of what was collected`}
      compared={
        before === null
          ? undefined
          : compare(totals.net - totals.cost, previous.net - previous.cost, {
              days: period.days,
              format: f.money,
            })
      }
    />
  );
}

/**
 * The three figures of the period, from the ledger's own totals: collected by card (net of refunds),
 * processing cost with its cost a page, and the margin. Each against the period before.
 */
export function RevenueFigures({
  revenue,
  choice,
}: {
  revenue: Revenue;
  choice: PeriodChoice;
}): ReactNode {
  const f = useFormat();
  const { totals, previous, period } = revenue;
  const say = (what: string) => tileLabel(what, choice, period);
  const days = period.days;
  const cost = compare(totals.cost, previous.cost, { days, format: f.money });
  return (
    <div className="ad-rfigs">
      <Figure
        lead
        label={say('Collected by card')}
        title="Card money net of refunds: paid invoices and card page packs. Never a list price."
        value={f.money(totals.net)}
        note={totals.refunded > 0 ? `after ${f.money(totals.refunded)} refunded` : undefined}
        compared={compare(totals.net, previous.net, { days, format: f.money })}
      />
      <Figure
        label={say('Processing cost')}
        title={COST_ESTIMATE_EXPLAINED}
        value={f.money(totals.cost)}
        note={totals.pages > 0 ? costWords(f, totals.cost / totals.pages) : 'no pages read'}
        compared={{ ...cost, tone: 'muted' }}
      />
      <MarginFigure revenue={revenue} label={say('Margin')} />
    </div>
  );
}
